import { useCallback } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { COLLECTIONS } from '../../config/collections';
import { isTestAccountUser } from '@officehub/shared';
import { useOrganization } from '../../contexts/OrganizationContext';

/**
 * A person as the application consumes them: one record, tag-aware,
 * keyed by email because that is what scheduler lanes and schedule
 * blocks use.
 *
 * Read from `users`, but the HR employee list is what DEFINES these people.
 * `employeeId`, `roleTags` and `isCurrentEmployee` are all projected onto the
 * login by the mirrorRoleTags trigger, because the employee record itself is
 * readable only by HR admins and coordinators — see that trigger's header.
 *
 * Note what is deliberately NOT here: the employee's actual status. `users`
 * is readable org-wide, so the mirror carries only the yes/no the pickers
 * need, never whether someone is on leave, interviewing, or was let go.
 */
export interface Person {
  readonly userId: string;
  /** HR employee doc id, when this login is linked to one. */
  readonly employeeId: string | null;
  readonly displayName: string;
  readonly email: string;
  readonly roleTags: readonly string[];
  /** users.isActive — whether the LOGIN is enabled. Not employment. */
  readonly isActive: boolean;
  /**
   * True when HR says this person currently works here.
   *
   * An ABSENT mirror flag counts as employed: it means "no HR record to
   * judge by", and shared mailboxes like service@onpointgen.com are
   * legitimate assignees that must not vanish from pickers. Only a positive
   * `false` from HR excludes someone — see isNotCurrentEmployee.
   */
  readonly isEmployed: boolean;
  /**
   * True only when HR positively says this person does not work here.
   *
   * Covers people who have LEFT and people who have not STARTED (candidate,
   * offer_sent). Named for what it actually detects rather than
   * "isFormerEmployee", which would quietly include never-started hires in
   * any future offboarding report that trusted the name.
   */
  readonly isNotCurrentEmployee: boolean;
  /** A QA / fixture login (the `isTestAccount` marker OR the #6188 email/name rule). */
  readonly isTestAccount: boolean;
  /**
   * A login that is not staff: the Investor role (any case) or `isExternal`.
   * Such a person can read some screens but is never assignable work.
   */
  readonly isExternalLogin: boolean;
  readonly photoURL?: string;
}

export interface PeopleFilters {
  /**
   * Role tag slugs to filter by.
   *
   * `undefined` (omitted) is the ONLY spelling of "no tag filter" — it
   * returns every org user matching `includeInactive`. An explicit
   * **empty array** means the opposite: "no tags selected", and
   * `fetchPeople` short-circuits to `[]` before a query is ever issued.
   *
   * Do NOT conflate the two. Treating `tags: []` as "no filter" was exactly
   * the SchedulerMatrix defect this hook exists to prevent: a user
   * deselecting every tag chip in `TagFilterControl` produced
   * `usePeople({ tags: [] })`, which used to fall through to "return
   * everyone" and silently resurrected the show-everyone-past-and-present
   * bug the tag-filter feature was built to fix.
   */
  readonly tags?: readonly string[];
  /**
   * When true, inactive users are included alongside active ones. Default
   * (false/undefined) returns active users only.
   *
   * Named (and inverted) deliberately vs. the old `active?: boolean` field:
   * `active: false` used to mean "include inactive too" — the opposite of
   * what the name implied. `includeInactive: true` says exactly what it does.
   */
  readonly includeInactive?: boolean;
  /**
   * When true, people HR records as not currently employed are included —
   * both those who have left and those who have not started. Default
   * excludes them.
   *
   * Set this for surfaces that resolve HISTORICAL references — an audit
   * trail, a past ticket's technician, a closed call log — where dropping a
   * former colleague makes their name render as a raw uid instead. Leave it
   * off for anything that assigns NEW work.
   *
   * Filtered client-side rather than in the query: combining an
   * isCurrentEmployee predicate with the existing tag/isActive filters would
   * need another composite index for a result set of a few dozen rows.
   */
  readonly includeNonEmployees?: boolean;
  /**
   * Use for a DROPDOWN people choose from: test logins are hidden. Default false = everyone (name lookups of
   * already-assigned records, scheduler lanes, HR admin). Applied per observer on the cached list (not part of the
   * query key), so it costs no extra read.
   */
  readonly pickableOnly?: boolean;
}

export const peopleKeys = {
  all: ['people'] as const,
  lists: () => [...peopleKeys.all, 'list'] as const,
  list: (orgId: string, filters: PeopleFilters) =>
    [...peopleKeys.lists(), orgId, filters] as const,
};

/** Converts a raw users document into the Person shape. */
export function mapUserToPerson(userId: string, data: Record<string, unknown>): Person {
  const email = typeof data.email === 'string' ? data.email.toLowerCase() : '';
  const displayName =
    (typeof data.displayName === 'string' && data.displayName) ||
    (typeof data.fullName === 'string' && data.fullName) ||
    email;
  const roleTags = Array.isArray(data.roleTags)
    ? data.roleTags.filter((t): t is string => typeof t === 'string')
    : [];

  // Absent flag = no HR record to judge by (shared mailbox, service account,
  // or a login HR has not linked). Those stay visible; only an explicit
  // `false` from the mirror removes someone. Anything other than a real
  // boolean is treated as absent rather than coerced.
  const isNotCurrentEmployee = data.isCurrentEmployee === false;

  return {
    userId,
    employeeId:
      typeof data.roleTagsSourceEmployeeId === 'string' ? data.roleTagsSourceEmployeeId : null,
    displayName,
    email,
    roleTags,
    isActive: data.isActive === true,
    isEmployed: !isNotCurrentEmployee,
    isNotCurrentEmployee,
    isTestAccount: isTestAccountUser(data as never),
    isExternalLogin:
      data.isExternal === true ||
      (typeof data.role === 'string' && data.role.trim().toLowerCase() === 'investor'),
    photoURL: typeof data.photoURL === 'string' ? data.photoURL : undefined,
  };
}

/**
 * Builds a Person from partial data, filling every field with a neutral
 * default.
 *
 * For the handful of places that must SYNTHESISE a person rather than read
 * one — a scheduler lane forced open for someone who has no matching user
 * record, and test fixtures. Those used to be object literals cast to
 * `Person`, so every new field on the interface broke them at compile time
 * (or, with a cast, silently produced an invalid person).
 *
 * Employment defaults to "no HR record to judge by" rather than to employed
 * or departed: a synthesised person has no employee record behind them by
 * definition, and that is exactly what a null status means.
 */
export function makePerson(overrides: Partial<Person> & { readonly email: string }): Person {
  return {
    userId: '',
    employeeId: null,
    displayName: overrides.email,
    roleTags: [],
    isActive: false,
    isEmployed: true,
    isNotCurrentEmployee: false,
    isTestAccount: false,
    isExternalLogin: false,
    ...overrides,
  };
}

/** True when no tags were requested, or the person carries at least one. */
export function matchesTags(person: Person, tags: readonly string[] | undefined): boolean {
  if (!tags || tags.length === 0) return true;
  return tags.some((t) => person.roleTags.includes(t));
}

// Firestore's `array-contains-any` accepts at most 30 values. The role tag
// vocabulary is small and admin-curated (technician / office_staff /
// management, plus whatever an org adds), so callers are not expected to
// approach this limit — but guard it explicitly rather than let Firestore
// reject the query with an opaque error.
const ARRAY_CONTAINS_ANY_LIMIT = 30;

/**
 * Rejects filter combinations that would hit Firestore at runtime with an
 * opaque error, so the failure is legible and happens before a query is
 * ever issued. Pure and exported so both combinations can be unit tested
 * without mocking Firestore.
 */
export function assertSupportedPeopleFilters(filters: PeopleFilters): void {
  const hasTags = !!filters.tags && filters.tags.length > 0;

  if (filters.includeInactive && hasTags) {
    // The only declared composite index for roleTags is
    // (organizationId, isActive, roleTags CONTAINS) — see
    // firestore.indexes.json. [organizationId, roleTags] is not a prefix of
    // that index, so `includeInactive: true` combined with a tag filter
    // would hit Firestore's FAILED_PRECONDITION at runtime.
    throw new Error(
      'usePeople: includeInactive combined with tags is not supported — no Firestore index ' +
        'covers (organizationId, roleTags) without isActive. Either drop includeInactive ' +
        '(active-only tag filtering is indexed) or drop tags.'
    );
  }

  if (hasTags && filters.tags!.length > ARRAY_CONTAINS_ANY_LIMIT) {
    throw new Error(
      `usePeople: at most ${ARRAY_CONTAINS_ANY_LIMIT} tags can be filtered at once (got ${filters.tags!.length}).`
    );
  }
}

async function fetchPeople(orgId: string, filters: PeopleFilters): Promise<Person[]> {
  // An explicit empty array is "no tags selected" and must match NOBODY —
  // not "no filter, return everyone". See the doc comment on
  // PeopleFilters.tags. Short-circuit before building/issuing any query so
  // this can never accidentally fall through to an unfiltered fetch.
  if (filters.tags && filters.tags.length === 0) {
    return [];
  }

  assertSupportedPeopleFilters(filters);

  const constraints = [where('organizationId', '==', orgId)];
  const hasTags = !!filters.tags && filters.tags.length > 0;

  if (!filters.includeInactive) {
    constraints.push(where('isActive', '==', true));
  }
  if (hasTags) {
    constraints.push(where('roleTags', 'array-contains-any', [...filters.tags!]));
  }

  const snap = await getDocs(query(collection(db, COLLECTIONS.USERS), ...constraints));
  return snap.docs
    .map((d) => mapUserToPerson(d.id, d.data()))
    // A login stays enabled long after someone leaves — nothing flips
    // users.isActive on termination. HR's employment status is the only
    // reliable signal, and it now rides along on the user doc.
    .filter((p) => (filters.includeNonEmployees ? true : !p.isNotCurrentEmployee))
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
}

/**
 * The tag-aware people source.
 *
 * Replaces useOrganizationMembers / useActiveUsers for surfaces that need
 * role filtering. Phase 1 consumers: SchedulerMatrix and ScheduleBlockModal.
 */
export function usePeople(filters: PeopleFilters = {}): UseQueryResult<Person[]> {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.organizationId ?? '';
  const { pickableOnly = false, ...fetchFilters } = filters;
  const select = useCallback((people: Person[]) => (pickableOnly ? people.filter((p) => !p.isTestAccount) : people), [pickableOnly]);

  return useQuery({
    select,
    queryKey: peopleKeys.list(orgId, fetchFilters),
    queryFn: () => fetchPeople(orgId, fetchFilters),
    enabled: !!orgId,
  });
}
